import { useRef, useState } from "react";
import { useEsAdmin } from "@/hooks/useEsAdmin";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Download, Paperclip, Plus, Loader2, ShieldAlert, ShieldCheck, PenLine, X, FileCheck2, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { descargarRespuestaBlob } from "@/lib/download";
import { useSeguridadAccess } from "@/hooks/useSeguridadAccess";
import { DocumentosSeleccionables } from "@/components/DocumentosSeleccionables";
import { SeleccionarTodosCheckbox } from "@/components/SeleccionarTodosCheckbox";
import { useTienePermiso, PERMISO_SUBIR_CERTIFICADO_FIRMADO } from "@/lib/sesion";
import {
  DetailLayout,
  DetailLoading,
  DetailError,
  Section,
  Field,
  StatusBadge,
  formatDate,
} from "@/components/DetailLayout";
import { FlujoStepper } from "@/components/FlujoStepper";
import { AsignacionPanel } from "@/components/AsignacionPanel";
import { UploadDocumentoModal } from "@/components/UploadDocumentoModal";
import { PaisesBadges } from "@/components/PaisBadge";

interface Parametros {
  voltajeEntrada: string | null; voltajeSalida: string | null;
  corrienteEntrada: string | null; corrienteSalida: string | null;
  frecuenciaEntrada: string | null; frecuenciaSalida: string | null;
  potenciaEntrada: string | null; potenciaSalida: string | null;
}
interface AccesorioDetalle extends Parametros { id: string; nombre: string; }
interface NormaDetalle { id: string; nombreNorma: string; esquema: string | null; nmx: string[]; }
interface FraccionDetalle { id: string; numero: string; nico: string | null; }
interface PaisRef { id: string; codigo: string; nombre: string; }
interface TransicionLog {
  ts: string;
  transicionCodigo: string | null;
  transicionNombre: string | null;
  origenCodigo: string | null;
  destinoCodigo: string;
  actorId: string | null;
  actorCorreo: string | null;
  motivo: string | null;
}

interface SolicitudDetalle {
  id: string;
  folio: string;
  fechaCreacion: string;
  titularId: string;
  titularRazonSocial: string | null;
  titularRfc: string | null;
  tramitadorId: string | null;
  tramitadorRazonSocial: string | null;
  tramitadorRfc: string | null;
  servicioCodigo: string;
  servicioNombre: string | null;
  otroServicio: string | null;
  productoNombre: string;
  marca: string;
  modelos: string | null;
  accesorios: string | null;
  producto: Parametros | null;
  listaAccesorios: AccesorioDetalle[];
  paisesOrigen: PaisRef[];
  paisesProcedencia: PaisRef[];
  fracciones: FraccionDetalle[];
  labNombre: string | null;
  labNumInforme: string | null;
  labModelosEvaluados: string | null;
  labFechaFinPruebas: string | null;
  labFechaEmision: string | null;
  modelosAgrupar: string | null;
  modeloRepresentativo: string | null;
  certAntNumDictamen: string | null;
  certAntModsAmpliar: string | null;
  certAntCertCumpl: string | null;
  normas: NormaDetalle[];
  observaciones: string | null;
  nombreRepresentante: string | null;
  condicionesUso: boolean;
  firmaTipo: string | null;
  firmaObjectKey: string | null;
  certificadoDisponible: boolean;
  certificadoNombre: string | null;
  cartaAceptacionDisponible: boolean;
  cartaAceptacionNombre: string | null;
  instanciaId: string | null;
  estadoCodigo: string | null;
  estadoNombre: string | null;
  cerrada: boolean;
  historico: TransicionLog[];
}

function toneFlujo(cerrada: boolean, estadoCodigo: string | null): "ok" | "info" | "warn" | "neutral" {
  if (!estadoCodigo) return "neutral";
  if (cerrada) return estadoCodigo === "ACEPTADA" ? "ok" : "warn";
  return "info";
}

const PARAM_LABELS: [keyof Parametros, string][] = [
  ["voltajeEntrada", "Voltaje entrada"], ["voltajeSalida", "Voltaje salida"],
  ["corrienteEntrada", "Corriente entrada"], ["corrienteSalida", "Corriente salida"],
  ["frecuenciaEntrada", "Frecuencia entrada"], ["frecuenciaSalida", "Frecuencia salida"],
  ["potenciaEntrada", "Potencia entrada"], ["potenciaSalida", "Potencia salida"],
];

function tieneParams(p: Parametros | null): boolean {
  return !!p && PARAM_LABELS.some(([k]) => p[k]);
}

export function SolicitudSeguridadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const esAdmin = useEsAdmin();
  const q = useQuery({
    queryKey: ["solicitud-seguridad-detalle", id],
    queryFn: async () =>
      (await api.get<SolicitudDetalle>(`/api/sector/seguridad/solicitudes/${id}/detalle-completo`)).data,
    enabled: !!id,
  });

  if (q.isLoading) return <DetailLoading />;
  if (q.error || !q.data) return <DetailError />;
  const s = q.data;

  return (
    <DetailLayout
      title={s.folio}
      subtitle={<>{s.productoNombre} · marca <span className="font-medium">{s.marca}</span></>}
      backTo="/seguridad"
      backLabel="Volver a Seguridad"
      badges={
        <>
          <StatusBadge tone={toneFlujo(s.cerrada, s.estadoCodigo)}>
            {s.estadoNombre ?? s.estadoCodigo ?? "sin flujo"}
          </StatusBadge>
          {s.cerrada && <StatusBadge tone="neutral">cerrada</StatusBadge>}
        </>
      }
      rawData={s}
    >
      <FlujoStepper estadoCodigo={s.estadoCodigo} cerrada={s.cerrada} />

      {/* Responsable interno por etapa (RF-03) — solo visible para internos */}
      <AsignacionPanel instanciaId={s.instanciaId} solicitudId={s.id} folio={s.folio} sector="seguridad" />

      <CertificadoSection
        solicitudId={s.id}
        estadoCodigo={s.estadoCodigo}
        certificadoDisponible={s.certificadoDisponible}
        certificadoNombre={s.certificadoNombre}
        cartaAceptacionDisponible={s.cartaAceptacionDisponible}
        cartaAceptacionNombre={s.cartaAceptacionNombre}
      />

      <Section title="Solicitud">
        <Field label="Folio" value={<span className="font-mono">{s.folio}</span>} />
        <Field label="Fecha de registro" value={formatDate(s.fechaCreacion, true)} />
        <Field label="Tipo de trámite" value={s.servicioNombre ?? s.servicioCodigo} />
        {s.otroServicio && <Field label='"Otro"' value={s.otroServicio} />}
      </Section>

      <Section title="Datos del solicitante">
        <Field
          label="Titular"
          span={2}
          value={
            <Link to={`/registros-legales/${s.titularId}`} className="text-primary-strong hover:underline">
              {s.titularRazonSocial}
            </Link>
          }
        />
        {s.titularRfc && <Field label="RFC titular" value={<span className="font-mono">{s.titularRfc}</span>} />}
        {s.tramitadorId && (
          <>
            <Field
              label="Tramitador externo"
              span={2}
              value={
                <Link to={`/registros-legales/${s.tramitadorId}`} className="text-primary-strong hover:underline">
                  {s.tramitadorRazonSocial}
                </Link>
              }
            />
            {s.tramitadorRfc && <Field label="RFC tramitador" value={<span className="font-mono">{s.tramitadorRfc}</span>} />}
          </>
        )}
      </Section>

      <Section title="Datos del producto">
        <Field label="Producto" value={s.productoNombre} />
        <Field label="Marca" value={s.marca} />
        {s.modelos && <Field label="Modelos" value={s.modelos} span={3} />}
        {s.accesorios && <Field label="Accesorios (texto)" value={s.accesorios} span={3} />}
        {tieneParams(s.producto) && (
          <Field
            label="Parámetros eléctricos"
            span={3}
            value={
              <dl className="grid gap-1 text-xs sm:grid-cols-4">
                {PARAM_LABELS.map(([k, label]) =>
                  s.producto && s.producto[k] ? <Row key={k} label={label} value={s.producto[k]!} /> : null,
                )}
              </dl>
            }
          />
        )}
        {s.paisesOrigen.length > 0 && (
          <Field
            label="País(es) de origen"
            span={3}
            value={
              <PaisesBadges paises={s.paisesOrigen} />
            }
          />
        )}
        {s.paisesProcedencia.length > 0 && (
          <Field
            label="País(es) de procedencia"
            span={3}
            value={
              <PaisesBadges paises={s.paisesProcedencia} />
            }
          />
        )}
      </Section>

      {s.listaAccesorios.length > 0 && (
        <Section title={`Accesorios (${s.listaAccesorios.length})`}>
          <Field
            label="Listado"
            span={3}
            value={
              <ul className="space-y-2">
                {s.listaAccesorios.map((a) => (
                  <li key={a.id} className="rounded border bg-surface-2 p-3">
                    <div className="font-medium text-fg">{a.nombre}</div>
                    <dl className="mt-1 grid gap-1 text-xs sm:grid-cols-4">
                      {PARAM_LABELS.map(([k, label]) => (a[k] ? <Row key={k} label={label} value={a[k]!} /> : null))}
                    </dl>
                  </li>
                ))}
              </ul>
            }
          />
        </Section>
      )}

      {s.fracciones.length > 0 && (
        <Section title={`Fracciones arancelarias (${s.fracciones.length})`}>
          <Field
            label="Fracciones"
            span={3}
            value={
              <div className="flex flex-wrap gap-1">
                {s.fracciones.map((f) => (
                  <StatusBadge key={f.id} tone="neutral">
                    <span className="font-mono">{f.numero}</span>{f.nico ? ` · NICO ${f.nico}` : ""}
                  </StatusBadge>
                ))}
              </div>
            }
          />
        </Section>
      )}

      {(s.labNombre || s.labNumInforme || s.labModelosEvaluados || s.labFechaFinPruebas || s.labFechaEmision) && (
        <Section title="Datos del laboratorio">
          {s.labNombre && <Field label="Laboratorio" value={s.labNombre} />}
          {s.labNumInforme && <Field label="No. de informe" value={<span className="font-mono">{s.labNumInforme}</span>} />}
          {s.labModelosEvaluados && <Field label="Modelos evaluados" value={s.labModelosEvaluados} span={3} />}
          {s.labFechaFinPruebas && <Field label="Fin de pruebas" value={formatDate(s.labFechaFinPruebas)} />}
          {s.labFechaEmision && <Field label="Emisión" value={formatDate(s.labFechaEmision)} />}
        </Section>
      )}

      {(s.modelosAgrupar || s.modeloRepresentativo || s.certAntNumDictamen || s.certAntModsAmpliar || s.certAntCertCumpl) && (
        <Section title="Agrupación y antecedentes">
          {s.modelosAgrupar && <Field label="Modelos a agrupar" value={s.modelosAgrupar} />}
          {s.modeloRepresentativo && <Field label="Modelo representativo" value={s.modeloRepresentativo} />}
          {s.certAntNumDictamen && <Field label="No. dictamen/cert. anterior" value={<span className="font-mono">{s.certAntNumDictamen}</span>} />}
          {s.certAntModsAmpliar && <Field label="Modelos a ampliar" value={s.certAntModsAmpliar} />}
          {s.certAntCertCumpl && <Field label="Certificado de cumplimiento" value={s.certAntCertCumpl} span={3} />}
        </Section>
      )}

      {s.normas.length > 0 && (
        <Section title={`Normas aplicables (${s.normas.length})`}>
          <Field
            label="Normas"
            span={3}
            value={
              <ul className="space-y-3">
                {s.normas.map((n) => (
                  <li key={n.id} className="rounded border bg-surface-2 p-3">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="font-mono text-sm font-semibold text-fg">{n.nombreNorma}</span>
                      {n.esquema && <span className="text-xs text-fg-muted">· {n.esquema}</span>}
                    </div>
                    {n.nmx.length > 0 && (
                      <div className="mt-1 text-xs text-fg-muted">
                        <span className="font-medium">NMX:</span> {n.nmx.join(" · ")}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            }
          />
        </Section>
      )}

      {(s.observaciones || s.nombreRepresentante || s.firmaTipo) && (
        <Section title="Observaciones y firma">
          {s.nombreRepresentante && <Field label="Representante" value={s.nombreRepresentante} />}
          <Field label="Condiciones de uso" value={s.condicionesUso ? "Aceptadas" : "No aceptadas"} />
          {s.firmaTipo && <Field label="Firma" value={s.firmaTipo} />}
          {s.observaciones && <Field label="Observaciones" span={3} value={<p className="whitespace-pre-line">{s.observaciones}</p>} />}
        </Section>
      )}

      <DocumentosSection
        solicitudId={s.id}
        title="Documentos del servicio"
        tipos={["SEG_DOCUMENTO_SERVICIO", "SEG_OTROS_DOCUMENTOS"]}
        uploadTitle="Adjuntar documento del servicio"
        uploadTag="SEGURIDAD"
        emptyText="Aún no hay documentos adjuntos."
      />
      <DocumentosSection
        solicitudId={s.id}
        title="Notificación de cumplimiento"
        tipos={["SEG_NOTIF_CUMPLIMIENTO"]}
        uploadTitle="Adjuntar notificación de cumplimiento"
        uploadTag="SEGURIDAD_CUMPLIMIENTO"
        uploadFijo="SEG_NOTIF_CUMPLIMIENTO"
        emptyText="Aún no hay notificaciones de cumplimiento."
      />
      <FirmaSection solicitudId={s.id} />

      <Section title={`Histórico de cambios de estado (${s.historico.length})`}>
        <Field
          label="Línea de tiempo"
          span={3}
          value={
            s.historico.length === 0 ? (
              <span className="text-sm text-fg-subtle">Sin transiciones registradas.</span>
            ) : (
              <ol className="space-y-2">
                {s.historico.map((h, i) => (
                  <li key={i} className="flex flex-wrap items-start gap-3 rounded border bg-surface p-3 text-sm">
                    <div className="text-xs text-fg-subtle">{formatDate(h.ts, true)}</div>
                    <div className="flex-1">
                      <div className="font-medium text-fg">{h.transicionNombre ?? h.transicionCodigo ?? "Transición"}</div>
                      <div className="text-xs text-fg-muted">
                        {h.origenCodigo ? <><span className="font-mono">{h.origenCodigo}</span> → </> : null}
                        <span className="font-mono text-fg">{h.destinoCodigo}</span>
                      </div>
                      {h.motivo && (
                        <div className="mt-1 whitespace-pre-line rounded bg-surface-2 p-2 text-xs text-fg-muted">{h.motivo}</div>
                      )}
                    </div>
                    {h.actorCorreo && <div className="shrink-0 text-xs text-fg-subtle">{h.actorCorreo}</div>}
                  </li>
                ))}
              </ol>
            )
          }
        />
      </Section>

      {/* IDs internos: solo para ADMIN_SICEN (soporte); confunden al usuario final */}
      {esAdmin && (
        <Section title="Metadatos">
          <Field label="ID" value={<span className="font-mono">{s.id}</span>} span={2} />
          <Field label="Instancia ID" value={<span className="font-mono">{s.instanciaId}</span>} />
        </Section>
      )}
    </DetailLayout>
  );
}

interface DocumentoResumen {
  documentoId: string;
  versionVigenteId: string;
  tipoCodigo: string;
  tipoNombre: string;
  nombreArchivo: string;
  sizeBytes: number;
  estatusVersion: string;
  antivirusEstado: string;
  fechaCarga: string;
}

const DOCS_QK = (solicitudId: string) => ["documentos-seguridad", solicitudId];

/** Documentos del contexto SOLICITUD_SEGURIDAD. Una sola query compartida por
    todas las secciones (react-query la deduplica por queryKey). */
function useDocumentos(solicitudId: string) {
  return useQuery<DocumentoResumen[]>({
    queryKey: DOCS_QK(solicitudId),
    queryFn: async () =>
      (await api.get<DocumentoResumen[]>(
        `/api/documentos/contexto?contextoTipo=SOLICITUD_SEGURIDAD&contextoId=${solicitudId}`,
      )).data,
  });
}

/** Lista de documentos con botón de descarga (URL presignada). */
/**
 * Lista de documentos del sector Seguridad. La selección múltiple y el ZIP los
 * pone {@link DocumentosSeleccionables}, que es común a todos los sectores; aquí
 * solo se añade la insignia de antivirus y la fecha de carga.
 */
function DocumentosLista({
  docs, solicitudId, onCambio,
}: {
  docs: DocumentoResumen[];
  solicitudId: string;
  onCambio: () => Promise<void>;
}) {
  return (
    <DocumentosSeleccionables
      docs={docs}
      onCambio={onCambio}
      contextoTipo="SOLICITUD_SEGURIDAD"
      contextoId={solicitudId}
      motivoDescarga="Descarga desde detalle de solicitud Seguridad"
      renderMeta={(d) => (
        <>
          <span>· {formatDate(d.fechaCarga, true)}</span>
          <AntivirusBadge estado={(d as DocumentoResumen).antivirusEstado} />
        </>
      )}
    />
  );
}

/**
 * Sección genérica de documentos filtrada por tipo. Cubre "Documentos del
 * servicio" y "Notificación de cumplimiento" del portlet ServiciosSeguridadLG.
 */
function DocumentosSection({
  solicitudId, title, tipos, uploadTitle, uploadTag, uploadFijo, emptyText,
}: {
  solicitudId: string;
  title: string;
  tipos: string[];
  uploadTitle: string;
  uploadTag: string;
  uploadFijo?: string;
  emptyText: string;
}) {
  const qc = useQueryClient();
  const [showUpload, setShowUpload] = useState(false);
  const q = useDocumentos(solicitudId);
  const docs = (q.data ?? []).filter((d) => tipos.includes(d.tipoCodigo));

  return (
    <section className="rounded-lg border bg-surface p-4 shadow-card">
      <div className="mb-3 flex items-center justify-between border-b pb-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
          {title}{docs.length > 0 ? ` (${docs.length})` : ""}
        </h2>
        <button
          type="button"
          onClick={() => setShowUpload(true)}
          className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-2.5 py-1 text-xs font-medium text-primary-strong transition hover:bg-primary-tint"
        >
          <Plus className="h-3.5 w-3.5" /> Adjuntar
        </button>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-fg-subtle">Cargando documentos…</p>
      ) : docs.length === 0 ? (
        <div className="rounded border border-dashed border-edge-strong p-4 text-center text-sm text-fg-subtle">
          <Paperclip className="mx-auto mb-1 h-5 w-5 text-fg-faint" />
          {emptyText}
        </div>
      ) : (
        <DocumentosLista
          docs={docs}
          solicitudId={solicitudId}
          onCambio={async () => { await qc.invalidateQueries({ queryKey: DOCS_QK(solicitudId) }); }}
        />
      )}

      <UploadDocumentoModal
        open={showUpload}
        title={uploadTitle}
        contextoTipo="SOLICITUD_SEGURIDAD"
        contextoId={solicitudId}
        tag={uploadTag}
        tipoCodigoFijo={uploadFijo}
        onClose={() => setShowUpload(false)}
        onUploaded={async () => {
          await qc.invalidateQueries({ queryKey: DOCS_QK(solicitudId) });
        }}
      />
    </section>
  );
}

/**
 * Sección Firma del representante (portlet: opcionFirma 1=dibujada, 2=adjunta).
 * Dibujada → PNG exportado del canvas; adjunta → imagen subida. Ambas se guardan
 * como documento tipo SEG_FIRMA; la modalidad va en la descripción.
 */
function FirmaSection({ solicitudId }: { solicitudId: string }) {
  const qc = useQueryClient();
  const [showDibujar, setShowDibujar] = useState(false);
  const [showAdjuntar, setShowAdjuntar] = useState(false);
  const q = useDocumentos(solicitudId);
  const firmas = (q.data ?? []).filter((d) => d.tipoCodigo === "SEG_FIRMA");
  const refrescar = () => qc.invalidateQueries({ queryKey: DOCS_QK(solicitudId) });

  return (
    <section className="rounded-lg border bg-surface p-4 shadow-card">
      <div className="mb-3 flex items-center justify-between border-b pb-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
          Firma del representante{firmas.length > 0 ? ` (${firmas.length})` : ""}
        </h2>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowDibujar(true)}
            className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-2.5 py-1 text-xs font-medium text-primary-strong transition hover:bg-primary-tint"
          >
            <PenLine className="h-3.5 w-3.5" /> Dibujar
          </button>
          <button
            type="button"
            onClick={() => setShowAdjuntar(true)}
            className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-2.5 py-1 text-xs font-medium text-primary-strong transition hover:bg-primary-tint"
          >
            <Plus className="h-3.5 w-3.5" /> Adjuntar
          </button>
        </div>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-fg-subtle">Cargando…</p>
      ) : firmas.length === 0 ? (
        <div className="rounded border border-dashed border-edge-strong p-4 text-center text-sm text-fg-subtle">
          Sin firma. Dibújala en pantalla o adjunta una imagen.
        </div>
      ) : (
        <DocumentosLista docs={firmas} solicitudId={solicitudId} onCambio={async () => { await refrescar(); }} />
      )}

      {showDibujar && (
        <SignaturePadModal
          solicitudId={solicitudId}
          onClose={() => setShowDibujar(false)}
          onSaved={async () => { setShowDibujar(false); await refrescar(); }}
        />
      )}
      <UploadDocumentoModal
        open={showAdjuntar}
        title="Adjuntar firma (imagen)"
        contextoTipo="SOLICITUD_SEGURIDAD"
        contextoId={solicitudId}
        tag="SEGURIDAD_FIRMA"
        tipoCodigoFijo="SEG_FIRMA"
        onClose={() => setShowAdjuntar(false)}
        onUploaded={async () => { setShowAdjuntar(false); await refrescar(); }}
      />
    </section>
  );
}

/** Modal con canvas para dibujar la firma; al guardar la sube como PNG (SEG_FIRMA). */
function SignaturePadModal({
  solicitudId, onClose, onSaved,
}: {
  solicitudId: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dibujando = useRef(false);
  const vacio = useRef(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = canvasRef.current!.getContext("2d")!;
    dibujando.current = true; vacio.current = false;
    const { x, y } = pos(e);
    ctx.beginPath(); ctx.moveTo(x, y);
    canvasRef.current!.setPointerCapture(e.pointerId);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!dibujando.current) return;
    const ctx = canvasRef.current!.getContext("2d")!;
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a";
    const { x, y } = pos(e);
    ctx.lineTo(x, y); ctx.stroke();
  };
  const end = () => { dibujando.current = false; };
  const limpiar = () => {
    const c = canvasRef.current!;
    c.getContext("2d")!.clearRect(0, 0, c.width, c.height);
    vacio.current = true;
  };

  const guardar = () => {
    if (vacio.current) { setError("Dibuja la firma antes de guardar."); return; }
    setError(null); setGuardando(true);
    canvasRef.current!.toBlob(async (blob) => {
      try {
        if (!blob) throw new Error("No se pudo generar la imagen.");
        const form = new FormData();
        form.append("archivo", new File([blob], "firma.png", { type: "image/png" }));
        form.append("tipoDocumentoCodigo", "SEG_FIRMA");
        form.append("contextoTipo", "SOLICITUD_SEGURIDAD");
        form.append("contextoId", solicitudId);
        form.append("descripcion", "Firma dibujada");
        await api.post("/api/documentos", form, { headers: { "Content-Type": "multipart/form-data" } });
        await onSaved();
      } catch (e: unknown) {
        const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail || (e as Error).message;
        setError(detail || "Error al guardar la firma.");
        setGuardando(false);
      }
    }, "image/png");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-surface shadow-overlay">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="text-base font-semibold">Dibujar firma</h3>
          <button onClick={onClose} disabled={guardando} className="cursor-pointer rounded p-1 text-fg-subtle transition hover:bg-surface-2">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 p-5">
          <canvas
            ref={canvasRef}
            width={400}
            height={180}
            onPointerDown={start}
            onPointerMove={move}
            onPointerUp={end}
            onPointerLeave={end}
            className="w-full touch-none rounded border border-edge-strong bg-surface-2"
          />
          <div className="flex items-center justify-between">
            <button type="button" onClick={limpiar} disabled={guardando} className="cursor-pointer text-xs text-fg-muted transition hover:text-fg">
              Limpiar
            </button>
            <span className="text-xs text-fg-faint">Dibuja con el mouse o el dedo</span>
          </div>
          {error && (
            <div className="flex items-start gap-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">
              <ShieldAlert className="mt-0.5 h-4 w-4" /><span>{error}</span>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t bg-surface-2 px-5 py-3">
          <button onClick={onClose} disabled={guardando} className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2">
            Cancelar
          </button>
          <button
            onClick={guardar}
            disabled={guardando}
            className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
          >
            {guardando ? "Guardando…" : "Guardar firma"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AntivirusBadge({ estado }: { estado: string }) {
  if (estado === "LIMPIO") {
    return (
      <span className="inline-flex items-center gap-1 text-estado-ok">
        <ShieldCheck className="h-3.5 w-3.5" /> limpio
      </span>
    );
  }
  if (estado === "PENDIENTE") {
    return <span className="text-estado-warn">· antivirus pendiente</span>;
  }
  return (
    <span className="inline-flex items-center gap-1 text-estado-err">
      <ShieldAlert className="h-3.5 w-3.5" /> {estado.toLowerCase()}
    </span>
  );
}


/**
 * Certificado firmado. Aparece solo cuando el flujo terminó (estado ACEPTADA).
 * Internos suben/reemplazan; internos y externos con visibilidad descargan.
 */
function CertificadoSection({
  solicitudId, estadoCodigo, certificadoDisponible, certificadoNombre,
  cartaAceptacionDisponible, cartaAceptacionNombre,
}: {
  solicitudId: string;
  estadoCodigo: string | null;
  certificadoDisponible: boolean;
  certificadoNombre: string | null;
  cartaAceptacionDisponible: boolean;
  cartaAceptacionNombre: string | null;
}) {
  const qc = useQueryClient();
  const { esInterno } = useSeguridadAccess();
  // Capacidad, no rol: la lista de quién puede vive en kernel.rol_permiso.
  const puedeSubirCertificado = useTienePermiso(PERMISO_SUBIR_CERTIFICADO_FIRMADO);
  // Qué documento se está subiendo en el modal (null = cerrado). RF-05.
  const [uploadTipo, setUploadTipo] = useState<"certificado" | "carta" | null>(null);

  if (estadoCodigo !== "ACEPTADA") return null;

  const completos = certificadoDisponible && cartaAceptacionDisponible;
  const onUploaded = async () => {
    setUploadTipo(null);
    await qc.invalidateQueries({ queryKey: ["solicitud-seguridad-detalle", solicitudId] });
  };

  return (
    <section className="rounded-lg border bg-surface p-4 shadow-card">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b pb-2">
        <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
          <FileCheck2 className="h-4 w-4 text-estado-ok" /> Documentos de cierre
        </h2>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${completos ? "bg-estado-ok-bg text-estado-ok" : "bg-estado-warn-bg text-estado-warn"}`}>
          {(certificadoDisponible ? 1 : 0) + (cartaAceptacionDisponible ? 1 : 0)}/2 documentos cargados
        </span>
      </div>

      <div className="space-y-2">
        <DocumentoCierreRow
          titulo="Certificado firmado"
          disponible={certificadoDisponible}
          nombre={certificadoNombre}
          urlDescarga={`/api/sector/seguridad/solicitudes/${solicitudId}/certificado`}
          nombreDescarga="Certificado-seguridad.pdf"
          esInterno={esInterno}
          puedeSubir={puedeSubirCertificado}
          onSubir={() => setUploadTipo("certificado")}
          textoPendienteExterno="El certificado aún no está disponible. Aparecerá aquí en cuanto el organismo lo emita."
        />
        <DocumentoCierreRow
          titulo="Carta de Aceptación"
          disponible={cartaAceptacionDisponible}
          nombre={cartaAceptacionNombre}
          urlDescarga={`/api/sector/seguridad/solicitudes/${solicitudId}/carta-aceptacion`}
          nombreDescarga="Carta-Aceptacion.pdf"
          esInterno={esInterno}
          puedeSubir={esInterno}
          onSubir={() => setUploadTipo("carta")}
          textoPendienteExterno="La Carta de Aceptación aún no está disponible."
        />
      </div>

      {esInterno && !completos && (
        <p className="mt-3 rounded bg-estado-warn-bg p-2 text-[11px] text-estado-warn">
          Para concluir esta etapa se requieren los dos documentos. La notificación de disponibilidad
          al cliente se habilita cuando ambos estén cargados.
        </p>
      )}

      {uploadTipo && (
        <CertificadoUploadModal
          solicitudId={solicitudId}
          tipo={uploadTipo}
          onClose={() => setUploadTipo(null)}
          onUploaded={onUploaded}
        />
      )}
    </section>
  );
}

/** Renglón de un documento de cierre (RF-05): estado, descarga y subir/reemplazar. */
function DocumentoCierreRow({
  titulo, disponible, nombre, urlDescarga, nombreDescarga, esInterno, puedeSubir, onSubir, textoPendienteExterno,
}: {
  titulo: string;
  disponible: boolean;
  nombre: string | null;
  urlDescarga: string;
  nombreDescarga: string;
  esInterno: boolean;
  /** Quién puede cargar el documento. Separado de `esInterno`: ver el estado
   *  del expediente y poder subirlo son cosas distintas. */
  puedeSubir: boolean;
  onSubir: () => void;
  textoPendienteExterno: string;
}) {
  const [descargando, setDescargando] = useState(false);

  const descargar = async () => {
    setDescargando(true);
    try {
      const resp = await api.get(urlDescarga, { responseType: "blob" });
      descargarRespuestaBlob(resp, nombre ?? nombreDescarga);
    } catch {
      // el backend registra el intento
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 rounded border p-3 ${disponible ? "border-estado-ok/25 bg-estado-ok-bg" : "border-edge bg-surface-2"}`}>
      <div className="flex min-w-0 flex-1 items-center gap-2 text-sm">
        <FileCheck2 className={`h-4 w-4 shrink-0 ${disponible ? "text-estado-ok" : "text-fg-faint"}`} />
        <div className="min-w-0">
          <div className={`font-medium ${disponible ? "text-estado-ok" : "text-fg-muted"}`}>{titulo}</div>
          {disponible ? (
            <div className="truncate text-xs text-fg-muted" title={nombre ?? undefined}>{nombre ?? nombreDescarga}</div>
          ) : (
            <div className="text-xs text-fg-subtle">
              {esInterno ? "Pendiente de cargar." : textoPendienteExterno}
            </div>
          )}
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        {disponible && (
          <button
            type="button"
            onClick={descargar}
            disabled={descargando}
            className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-xs font-medium text-white transition hover:bg-primary-hover disabled:opacity-50"
          >
            {descargando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Descargar
          </button>
        )}
        {puedeSubir && (
          <button
            type="button"
            onClick={onSubir}
            className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-2.5 py-1 text-xs font-medium text-primary-strong transition hover:bg-primary-tint"
          >
            <Upload className="h-3.5 w-3.5" /> {disponible ? "Reemplazar" : "Subir"}
          </button>
        )}
      </div>
    </div>
  );
}

interface DestInterno { id: string; correo: string; nombre: string; }
interface DestRelacionado extends DestInterno {
  tipo: string;
  rlRazonSocial: string;
  /** «Representante legal» o «Usuario autorizado». */
  relacion?: string;
}

/** Modal para subir el certificado firmado (PDF) y elegir a quién notificar. */
function CertificadoUploadModal({
  solicitudId, tipo, onClose, onUploaded,
}: {
  solicitudId: string;
  /** certificado = con notificación opcional; carta = solo el archivo (RF-05). */
  tipo: "certificado" | "carta";
  onClose: () => void;
  onUploaded: () => Promise<void>;
}) {
  const esCarta = tipo === "carta";
  const [file, setFile] = useState<File | null>(null);
  const [notificarA, setNotificarA] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const qDest = useQuery<{ internos: DestInterno[]; relacionados: DestRelacionado[] }>({
    queryKey: ["destinatarios-notificacion", solicitudId],
    queryFn: async () =>
      (await api.get(`/api/sector/seguridad/solicitudes/${solicitudId}/destinatarios-notificacion`)).data,
    enabled: !esCarta,
  });

  const toggle = (id: string) =>
    setNotificarA((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  // Una gestoría puede tener quince personas autorizadas; marcarlas de una en
  // una era el trabajo que este control ahorra.
  const toggleTodos = (ids: string[], marcar: boolean) =>
    setNotificarA((p) => (marcar
      ? Array.from(new Set([...p, ...ids]))
      : p.filter((x) => !ids.includes(x))));


  const subir = async () => {
    if (!file) { setError(esCarta ? "Selecciona el PDF de la Carta de Aceptación." : "Selecciona el PDF del certificado."); return; }
    setError(null);
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (!esCarta) notificarA.forEach((id) => form.append("destinatariosUserIds", id));
      const endpoint = esCarta
        ? `/api/sector/seguridad/solicitudes/${solicitudId}/carta-aceptacion`
        : `/api/sector/seguridad/solicitudes/${solicitudId}/certificado`;
      await api.post(endpoint, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await onUploaded();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data?.detail ||
        (e as { response?: { data?: { title?: string } } }).response?.data?.title ||
        (e as Error).message;
      setError(detail || (esCarta ? "No se pudo subir la Carta de Aceptación." : "No se pudo subir el certificado."));
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-surface shadow-overlay">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="text-base font-semibold">{esCarta ? "Subir Carta de Aceptación" : "Subir certificado firmado"}</h3>
          <button onClick={onClose} disabled={loading} className="cursor-pointer rounded p-1 text-fg-subtle transition hover:bg-surface-2">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 p-5">
          <div>
            <label className="text-xs uppercase tracking-wide text-fg-subtle">
              Archivo PDF <span className="text-estado-err">*</span>
            </label>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setError(null); }}
              disabled={loading}
              className="mt-1 block w-full text-sm file:mr-3 file:cursor-pointer file:rounded file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:text-white hover:file:bg-primary-hover"
            />
          </div>

          {!esCarta && (
          <>
          <div className="rounded border bg-surface">
            <div className="border-b px-2 py-1.5 text-xs uppercase tracking-wide text-fg-subtle">
              Notificar por correo a ({notificarA.length} seleccionado{notificarA.length === 1 ? "" : "s"})
            </div>
            <div className="max-h-48 overflow-y-auto p-2 text-sm">
              {qDest.isLoading && <div className="text-xs text-fg-subtle">Cargando destinatarios…</div>}
              {qDest.data && (
                <>
                  {qDest.data.internos.length > 0 && (
                    <>
                      <div className="mt-1 mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-fg-faint">
                        Internos NPMX
                        <SeleccionarTodosCheckbox className="ml-auto" ids={qDest.data.internos.map((u) => u.id)}
                          seleccionados={notificarA} onToggleTodos={toggleTodos} />
                      </div>
                      {qDest.data.internos.map((u) => (
                        <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-surface-2">
                          <input type="checkbox" checked={notificarA.includes(u.id)} onChange={() => toggle(u.id)} />
                          <span className="font-medium">{u.nombre || u.correo}</span>
                          <span className="text-fg-subtle">— {u.correo}</span>
                        </label>
                      ))}
                    </>
                  )}
                  {qDest.data.relacionados.length > 0 && (
                    <>
                      <div className="mt-3 mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-fg-faint">
                        Solicitante y empresa tramitadora
                        <SeleccionarTodosCheckbox className="ml-auto" ids={qDest.data.relacionados.map((u) => u.id)}
                          seleccionados={notificarA} onToggleTodos={toggleTodos} />
                      </div>
                      {qDest.data.relacionados.map((u) => (
                        <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-surface-2">
                          <input type="checkbox" checked={notificarA.includes(u.id)} onChange={() => toggle(u.id)} />
                          <span className="font-medium">{u.nombre || u.correo}</span>
                          <span className="text-fg-subtle">— {u.correo}</span>
                          <span className="ml-auto text-[10px] text-fg-faint">{u.relacion ?? u.tipo} · {u.rlRazonSocial}</span>
                        </label>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          <p className="rounded bg-estado-warn-bg p-2 text-[11px] text-estado-warn">
            Si seleccionas destinatarios, se les enviará un correo real avisando que el certificado está
            disponible. Para notificar deben estar cargados el certificado y la Carta de Aceptación.
          </p>
          </>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">
              <ShieldAlert className="mt-0.5 h-4 w-4" /><span>{error}</span>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t bg-surface-2 px-5 py-3">
          <button onClick={onClose} disabled={loading} className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2">
            Cancelar
          </button>
          <button
            onClick={subir}
            disabled={loading || !file}
            className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
          >
            <Upload className="h-4 w-4" />
            {loading ? "Subiendo…" : esCarta ? "Subir carta" : "Subir certificado"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="font-semibold text-fg-muted">{label}:</dt>
      <dd className="text-fg">{value}</dd>
    </div>
  );
}
